import { doc, getDoc } from 'firebase/firestore';
import { format } from 'date-fns';
import { db } from '../config/firebase';
import { createInvoiceFromTemplate } from './excelService';
import { convertExcelToPDF } from './pdfService';

/**
 * Maps a stored invoice onto the flat shape the Excel templates expect. The row
 * on screen only carries what the table needs, so the full document is read back
 * from Firestore first.
 */
const toInvoiceItem = (complete: any, fallbackBroker: string): any => ({
  broker: complete.broker || fallbackBroker,
  invoiceNumber: complete.invoiceNumber || '',
  orderNumber: complete.orderNumber || '',
  loadNumber: complete.loadNumber || '',
  terminal: complete.terminal || '',
  destination: complete.destination || '',
  driver: complete.driver || '',
  gross: complete.gross?.toString() || '0',
  net: complete.net?.toString() || '0',
  rate: complete.rate?.toString() || '0',
  fuelSurchargePercent: complete.fuelSurchargePercent?.toString() || '0',
  totalDem: complete.totalDem?.toString() || '0',
  freightRev: complete.freightRev || 0,
  fuelSurcharge: complete.fuelSurcharge?.toString() || '0',
  demurrageCharge: complete.demurrageCharge || 0,
  totalCharges: complete.totalCharges || 0,
  notes: complete.notes || '',

  date:
    complete.date ||
    (complete.createdAt?.toDate ? format(complete.createdAt.toDate(), 'yyyy-MM-dd') : ''),
  bolNumber: complete.bolNumber || '',
  terminalArrivalTime: complete.terminalArrivalTime || '',
  terminalDepartTime: complete.terminalDepartTime || '',
  deliveryArrivalTime: complete.deliveryArrivalTime || '',
  deliveryDepartTime: complete.deliveryDepartTime || '',

  invoiceDate: complete.invoiceDate || '',
  dueDate: complete.dueDate || '',
  miles: complete.miles || '',
  customer: complete.customer || '',
  address: complete.address || '',
  cityStateZip: complete.cityStateZip || '',

  invoiceSheet:
    complete.invoiceSheet || (complete.broker === 'Fuel Masters' ? 'Fuel Masters' : 'Freight Warriors'),
  rowNumber: complete.rowNumber || '4',
  pumpEnabled: complete.pumpEnabled || false,
  pumpCount: complete.pumpCount?.toString() || '1',
  splitShareAmount: complete.splitShareAmount || 0
});

const invoiceFileName = (invoice: any, extension: string) =>
  `invoice_${invoice.invoiceNumber || invoice.orderNumber || invoice.loadNumber || 'unknown'}.${extension}`;

const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);
};

const buildExcel = async (invoice: any): Promise<Blob> => {
  if (!invoice.broker) {
    throw new Error('No broker information found for this invoice');
  }

  const snapshot = await getDoc(doc(db, 'invoices', invoice.id));
  if (!snapshot.exists()) {
    throw new Error('Invoice not found in database');
  }

  return createInvoiceFromTemplate(invoice.broker, toInvoiceItem(snapshot.data(), invoice.broker));
};

export const downloadInvoiceExcel = async (invoice: any): Promise<void> => {
  saveBlob(await buildExcel(invoice), invoiceFileName(invoice, 'xlsx'));
};

export const downloadInvoicePdf = async (invoice: any): Promise<void> => {
  // The PDF is the same Excel invoice, rendered.
  const pdfBlob = await convertExcelToPDF(await buildExcel(invoice));
  saveBlob(pdfBlob, invoiceFileName(invoice, 'pdf'));
};
